import {
  CheckCircle2,
  Circle,
  CircleDashed,
  CircleDot,
  CircleSlash,
  Clock,
  Eye,
  Loader2,
  PauseCircle,
  XCircle,
  type LucideIcon,
} from 'lucide-react';
import type { StatusRegistry, StatusTone, StatusVocabulary } from '@shining-technologies/ui';

/**
 * The admin's status vocabulary for the UI kit's `StatusBadge`, provided app-wide
 * by `StatusRegistryProvider` in app/providers.tsx. Use it as:
 *
 *   import { StatusBadge } from '@shining-technologies/ui';
 *   <StatusBadge type="admin" status={row.original.status} />
 *
 * It is the legacy components/data/StatusBadge.tsx table re-expressed in kit
 * tones (legacy Badge variant → kit tone: default → primary, secondary/outline →
 * neutral). Status is still conveyed by icon + text + colour, never colour alone
 * (a11y). An unknown status renders neutral with a prettified label
 * (`in_progress` → "In progress"), exactly like the legacy fallback.
 */

function def(label: string, tone: StatusTone, Icon: LucideIcon, spin = false) {
  return {
    label,
    tone,
    icon: <Icon className={spin ? 'size-3 animate-spin' : 'size-3'} aria-hidden />,
  };
}

export const ADMIN_STATUSES: StatusVocabulary = {
  active: def('Active', 'success', CheckCircle2),
  ready: def('Ready', 'success', CheckCircle2),
  connected: def('Connected', 'success', CheckCircle2),
  ok: def('Ok', 'success', CheckCircle2),
  healthy: def('Healthy', 'success', CheckCircle2),
  completed: def('Completed', 'success', CheckCircle2),
  paid: def('Paid', 'success', CheckCircle2),
  processing: def('Processing', 'primary', Loader2, true),
  live: def('Live', 'primary', Loader2, true),
  ended: def('Ended', 'neutral', CheckCircle2),
  cancelled: def('Cancelled', 'destructive', CircleSlash),
  scheduled: def('Scheduled', 'neutral', Clock),
  pending: def('Pending', 'neutral', Clock),
  queued: def('Queued', 'neutral', Clock),
  suspended: def('Suspended', 'warning', PauseCircle),
  paused: def('Paused', 'warning', PauseCircle),
  degraded: def('Degraded', 'warning', PauseCircle),
  disabled: def('Disabled', 'neutral', CircleSlash),
  failed: def('Failed', 'destructive', XCircle),
  error: def('Error', 'destructive', XCircle),
  down: def('Down', 'destructive', XCircle),
  // The dashboard's sev1 incident badge. Missing from the legacy table too, so
  // it fell through to the neutral unknown-status badge.
  critical: def('Critical', 'destructive', XCircle),
  deleted: def('Deleted', 'destructive', CircleSlash),
  overdue: def('Overdue', 'destructive', XCircle),
  // Tracker work-item statuses (todo / in_progress / in_review / done / …).
  todo: def('Todo', 'neutral', Circle),
  backlog: def('Backlog', 'neutral', CircleDashed),
  open: def('Open', 'neutral', Circle),
  in_progress: def('In progress', 'primary', CircleDot),
  in_review: def('In review', 'warning', Eye),
  review: def('Review', 'warning', Eye),
  blocked: def('Blocked', 'destructive', XCircle),
  done: def('Done', 'success', CheckCircle2),
  closed: def('Closed', 'success', CheckCircle2),
  resolved: def('Resolved', 'success', CheckCircle2),
};

export const ADMIN_STATUS_REGISTRY: StatusRegistry = { admin: ADMIN_STATUSES };
